import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, SlidersHorizontal, Map, Calculator, Network, TrendingUp, ArrowRight, Download } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Section";
import { Button } from "../components/ui/button";
import { useGov } from "../governance/GovContext";
import { saveCombined, hasReportData } from "../governance/govPdf";
import ReportShare from "../components/governance/ReportShare";
import ReadinessAssessment from "../components/governance/ReadinessAssessment";
import PolicyBuilder from "../components/governance/PolicyBuilder";
import FrameworkExplorer from "../components/governance/FrameworkExplorer";
import QaCalculator from "../components/governance/QaCalculator";
import DeploymentConfigurator from "../components/governance/DeploymentConfigurator";
import RoiCalculator from "../components/governance/RoiCalculator";

const TOOLS = [
  { key: "assess", label: "Readiness Assessment", short: "Assess", icon: ClipboardCheck, desc: "How ready is your organisation for AI governance? A 10–12 question maturity profile." },
  { key: "policy", label: "Policy Control Builder", short: "Configure Policy", icon: SlidersHorizontal, desc: "Build an example norse3 policy with a visual IF / THEN sandbox and test it." },
  { key: "framework", label: "Regulatory Framework Explorer", short: "Map Governance", icon: Map, desc: "See how governance themes connect to operational controls and evidence." },
  { key: "qa", label: "QA Coverage & Resource Calculator", short: "Model Operations", icon: Calculator, desc: "Model manual QA against an illustrative risk-based screening approach." },
  { key: "roi", label: "ROI & Value Calculator", short: "Build the Case", icon: TrendingUp, desc: "Model hours saved, cost avoided and the coverage gap you could close." },
  { key: "deploy", label: "Deployment Configurator", short: "Explore Deployment", icon: Network, desc: "See where norse3 could sit in your environment and an indicative architecture." },
];

export default function GovernanceTools() {
  const [active, setActive] = useState(null);
  const topRef = useRef(null);
  const gov = useGov();
  const canReport = hasReportData(gov);

  useEffect(() => { document.title = "Governance Tools | norse3"; }, []);

  const go = (key) => { setActive(key); setTimeout(() => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30); };

  return (
    <div>
      <PageHero
        eyebrow="Governance tools"
        title="Assess your environment. See where norse3 fits."
        subtitle="Five connected, no-registration tools for compliance, risk, security and technology teams. Assess your readiness, configure a policy, map governance, model operations and explore deployment. Educational only — not legal, regulatory or compliance advice."
      />

      <div ref={topRef} />
      <div className="sticky top-16 z-30 border-b border-white/10 bg-navy-950/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-wrap items-center gap-2 py-3">
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setActive(null)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${active === null ? "bg-primary/12 text-foreground border border-primary/40" : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"}`}>Overview</button>
              {TOOLS.map((t, i) => {
                const on = active === t.key; const Icon = t.icon;
                return (
                  <button key={t.key} onClick={() => go(t.key)} className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${on ? "bg-primary/12 text-foreground border border-primary/40" : "text-muted-foreground hover:text-foreground hover:bg-white/5 border border-transparent"}`}>
                    <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[0.7rem] ${on ? "bg-primary text-primary-foreground" : "bg-white/10"}`}>{i + 1}</span>
                    <Icon className="h-4 w-4" /><span className="hidden lg:inline">{t.short}</span>
                  </button>
                );
              })}
            </div>
            <Button size="sm" variant="outline" disabled={!canReport} onClick={() => saveCombined(gov)} data-testid="gov-download-full-report" className="hidden sm:inline-flex ml-auto shrink-0 border-primary/40 bg-transparent hover:bg-primary/10 text-primary disabled:opacity-40">
              <Download className="mr-1.5 h-4 w-4" /> Full report
            </Button>
          </div>
        </div>
      </div>

      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {active === null && (
            <Reveal>
              <p className="text-sm text-muted-foreground">Recommended journey: <span className="text-foreground">Assess → Configure Policy → Map Governance → Model Operations → Explore Deployment</span></p>
              {canReport && (
                <div className="mt-4">
                  <ReportShare />
                </div>
              )}
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {TOOLS.map((t, i) => {
                  const Icon = t.icon;
                  return (
                    <Reveal key={t.key} delay={i * 70}>
                      <button onClick={() => go(t.key)} className="card-rise text-left w-full h-full rounded-lg border border-white/10 bg-navy-900/60 p-6 hover:border-primary/40">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                        <h3 className="mt-4 text-lg font-semibold text-foreground">{t.label}</h3>
                        <p className="mt-2 text-sm text-muted-foreground">{t.desc}</p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">Open tool <ArrowRight className="h-4 w-4" /></span>
                      </button>
                    </Reveal>
                  );
                })}
              </div>
            </Reveal>
          )}
          {active && (
            <Reveal key={active}>
              <h2 className="text-2xl font-bold text-foreground">{TOOLS.find((t) => t.key === active).label}</h2>
              <p className="mt-2 text-muted-foreground max-w-2xl">{TOOLS.find((t) => t.key === active).desc}</p>
              <div className="mt-6">
                {active === "assess" && <ReadinessAssessment onGoto={go} />}
                {active === "policy" && <PolicyBuilder />}
                {active === "framework" && <FrameworkExplorer />}
                {active === "qa" && <QaCalculator />}
                {active === "roi" && <RoiCalculator />}
                {active === "deploy" && <DeploymentConfigurator />}
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
